import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import api, { API_ENDPOINTS } from '../config/api';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  User,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  MessageCircle,
  Eye,
  Star,
  MapPin,
  Briefcase,
  Loader2
} from 'lucide-react';

const JobApplicationsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { socket } = useSocket();
  const queryClient = useQueryClient();
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Fetch job details
  const { data: job, isLoading: jobLoading } = useQuery(
    ['job', id],
    () => api.get(API_ENDPOINTS.JOBS.DETAIL(id)),
    {
      select: (data) => data.data,
      enabled: !!id,
    }
  );

  // Fetch job applications with auto-refresh
  const { data: applicationsData, isLoading: applicationsLoading, refetch: refetchApplications } = useQuery(
    ['jobApplications', id],
    () => api.get(API_ENDPOINTS.JOBS.APPLICATIONS(id)),
    {
      select: (data) => data.data,
      enabled: !!id,
      staleTime: 10000, // Consider data stale after 10 seconds
      refetchInterval: 15000, // Auto-refetch every 15 seconds
      refetchOnWindowFocus: true, // Refetch when window regains focus
      refetchOnMount: true, // Always refetch on mount
    }
  );

  const applications = applicationsData?.applications || [];
  // Use job from applicationsData if available (has status counts), otherwise use job from detail query
  const displayJob = applicationsData?.job || job;

  // Listen for real-time updates via socket
  useEffect(() => {
    if (!socket || !id) return;

    const handleJobUpdated = (data) => {
      if (data.jobId === id || data._id === id) {
        // Refetch applications when job is updated
        refetchApplications();
      }
    };

    const handleApplicationUpdated = (data) => {
      if (data.jobId === id) {
        // Refetch applications when an application is updated
        refetchApplications();
      }
    };

    socket.on('job:updated', handleJobUpdated);
    socket.on('application:updated', handleApplicationUpdated);
    socket.on('application:approved', handleApplicationUpdated);
    socket.on('application:rejected', handleApplicationUpdated);

    return () => {
      socket.off('job:updated', handleJobUpdated);
      socket.off('application:updated', handleApplicationUpdated);
      socket.off('application:approved', handleApplicationUpdated);
      socket.off('application:rejected', handleApplicationUpdated);
    };
  }, [socket, id, refetchApplications]);

  // Approve application mutation
  const approveApplicationMutation = useMutation(
    (applicationId) => api.post(API_ENDPOINTS.JOBS.APPROVE_APPLICATION(id, applicationId)),
    {
      onSuccess: async (data) => {
        toast.success('Application approved successfully!');
        // Invalidate and refetch immediately
        await Promise.all([
          queryClient.invalidateQueries(['jobApplications', id]),
          queryClient.invalidateQueries(['job', id])
        ]);
        // Refetch applications data immediately
        refetchApplications();
        setSelectedApplication(null);
        // Navigate to chat if chatId is provided
        if (data.data?.chat) {
          navigate(`/chats/${data.data.chat}`);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to approve application');
      }
    }
  );

  // Reject application mutation
  const rejectApplicationMutation = useMutation(
    (applicationId) => api.post(API_ENDPOINTS.JOBS.REJECT_APPLICATION(id, applicationId)),
    {
      onSuccess: async () => {
        toast.success('Application rejected');
        // Invalidate and refetch immediately
        await Promise.all([
          queryClient.invalidateQueries(['jobApplications', id]),
          queryClient.invalidateQueries(['job', id])
        ]);
        // Refetch applications data immediately
        refetchApplications();
        setSelectedApplication(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to reject application');
      }
    }
  );

  const handleApprove = (applicationId) => {
    approveApplicationMutation.mutate(applicationId);
  };

  const handleReject = (applicationId) => {
    rejectApplicationMutation.mutate(applicationId);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      accepted: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      'in-progress': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
  };

  const formatBudget = (budget) => {
    if (!budget) return 'Budget not specified';
    if (budget.fixed) return `$${budget.fixed.toFixed(2)}`;
    if (budget.min && budget.max) return `$${budget.min.toFixed(2)} - $${budget.max.toFixed(2)}`;
    if (budget.min) return `$${budget.min.toFixed(2)}+`;
    return 'Budget negotiable';
  };

  if (jobLoading || applicationsLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-accent"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            Job not found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The job you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/jobs')}
            className="btn-primary"
          >
            Browse Jobs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-heading font-bold text-heading">
              Job Applications
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Review and manage applications for "{displayJob?.title || job?.title}"
            </p>
          </div>
        </div>

        {/* Job Info */}
        <div className="card p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {displayJob?.title || job?.title}
              </h2>
              <div 
                className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-2 prose prose-sm dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: (displayJob || job)?.description }}
              />
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center space-x-1">
                  <DollarSign className="w-4 h-4" />
                  <span>{formatBudget((displayJob || job)?.budget)}</span>
                </div>
                {(displayJob || job)?.duration && (displayJob || job).duration.value && (displayJob || job).duration.unit && (
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{(displayJob || job).duration.value} {(displayJob || job).duration.unit}</span>
                  </div>
                )}
                {(displayJob || job)?.location && (
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>{(displayJob || job).location.remote ? 'Remote' : (displayJob || job).location.city || 'Location not specified'}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-wrap gap-2">
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                (displayJob || job)?.type === 'full-time' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                (displayJob || job)?.type === 'part-time' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                (displayJob || job)?.type === 'freelance' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
              }`}>
                {((displayJob || job)?.type || '').charAt(0).toUpperCase() + ((displayJob || job)?.type || '').slice(1)}
              </span>
              {/* Show application status counts */}
              {(displayJob || job)?.applicationStatusCounts && (
                <>
                  {(displayJob || job).applicationStatusCounts.completed > 0 && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      Completed ({(displayJob || job).applicationStatusCounts.completed})
                    </span>
                  )}
                  {(displayJob || job).applicationStatusCounts.active > 0 && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      Active ({(displayJob || job).applicationStatusCounts.active})
                    </span>
                  )}
                  {(displayJob || job).applicationStatusCounts.pending > 0 && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                      Pending ({(displayJob || job).applicationStatusCounts.pending})
                    </span>
                  )}
                  {(displayJob || job).applicationStatusCounts.rejected > 0 && (
                    <span className="px-3 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                      Rejected ({(displayJob || job).applicationStatusCounts.rejected})
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Applications List */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Applications ({applications.length})
          </h3>
        </div>

        {applications.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No applications yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              No one has applied for this job yet. Share the job to get more applications.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {applications.map((application) => (
              <div key={application._id} className="card p-6">
                <div className="flex items-start justify-between mb-4">
                  <Link 
                    to={`/profile/${application.talent?.username || application.talent?._id || application.talent}`}
                    className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
                  >
                    {application.talent?.profile?.avatar ? (
                      <img 
                        src={application.talent.profile.avatar} 
                        alt={application.talent.username}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-gray-500" />
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        {application.talent?.username || 'Unknown User'}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Applied {new Date(application.appliedAt).toLocaleDateString()}
                      </p>
                    </div>
                  </Link>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(application.status)}`}>
                    {application.status.charAt(0).toUpperCase() + application.status.slice(1)}
                  </span>
                </div>

                <div className="space-y-3 mb-4">
                  <div>
                    <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                      Cover Letter
                    </h5>
                    <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                      {application.coverLetter}
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                        Bid Amount
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        ${application.bidAmount?.toFixed(2) || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                        Estimated Duration
                      </h5>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {application.estimatedDuration || 'Not specified'} days
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {(application.status === 'accepted' || application.status === 'in-progress' || application.status === 'completed') && (
                      <Link
                        to={application.chatId ? `/chats/${application.chatId}` : "/chats"}
                        className="btn-primary text-sm flex items-center"
                      >
                        <MessageCircle className="w-4 h-4 mr-2" />
                        Open Chat
                      </Link>
                    )}
                  </div>

                  {application.status === 'pending' && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleReject(application._id)}
                        disabled={rejectApplicationMutation.isLoading || approveApplicationMutation.isLoading}
                        className="btn-outline text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </button>
                      <button
                        onClick={() => handleApprove(application._id)}
                        disabled={approveApplicationMutation.isLoading}
                        className="btn-primary"
                      >
                        {approveApplicationMutation.isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Approving...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Approve
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobApplicationsPage;